import PropTypes from 'prop-types';
import React from 'react';
import api from '../../../Api';
import makeConnectTo from '../../util/makeConnectTo';
import moment from 'moment';
import queryString from 'query-string';
import { withIsMobile } from 'treeline-react-core-components/WindowSizeProvider';
import { Translate } from '@above-the-treeline/localization';
import AlreadyRegisteredError from '../../Anonymous/AnonymousHeader/RegistrationModal/RegistrationForm/AlreadyRegisteredError/AlreadyRegisteredError';
import { ApplicationIdentifierEnum } from '../../../enums/ApplicationIdentifierEnum';
import { isFunction } from 'lodash-es';
import UserPrivilegesEnum from '../enums/UserPrivilegesEnum';
import {
	setGainsightCookieForAnonUser,
	setGainsightIdentity,
} from '../../../util/gainsight';

const UserContext = React.createContext();

const name = 'userDataStore';

export const useUser = () => React.useContext(UserContext);

export const connectToUser = makeConnectTo(UserContext, name);

const anonymousUserId = 0;

class UserDataStore extends React.Component {
	static propTypes = {
		children: PropTypes.oneOfType([PropTypes.element, PropTypes.func]),
		isMobile: PropTypes.func,
	};

	state = {
		user: {},
		organization: {},
		userAvatar: {},
		joinedCommunities: [],
		isLoading: true,
		sessionId: '',
		isAnonymous: false,
		isAuthorizedToPostEventsToGenreCommunities: true,
		isUnsubscribedFromFriendEmail: false,
		region: null,
	};

	render() {
		const userDataStore = this.getStore();
		return (
			<UserContext.Provider value={userDataStore}>
				{typeof this.props.children === 'function'
					? this.props.children(userDataStore)
					: this.props.children}
			</UserContext.Provider>
		);
	}

	componentDidMount() {
		this.fetchUser(() => {
			this.fetchJoinedCommunities();
			this.fetchUnsubscribedFromFriendEmailPreference();
		});
	}

	componentWillUnmount() {
		if (this.joinedCommunitiesFetch && this.joinedCommunitiesFetch.cancel) {
			this.joinedCommunitiesFetch.cancel();
		}
	}

	createSessionId = user => {
		const formattedDate = moment().format('YYMMDDhhmm');
		const sessionId = `${formattedDate}-${
			user ? user.id : anonymousUserId
		}`;
		return sessionId;
	};

	postUsageTracking = siteArea => {
		const endpoint = `/api/v1/usage`;
		const communitySiteContext = 3;
		const usageInfo = {
			siteContext: communitySiteContext,
			siteArea: siteArea,
			sessionId: this.state.sessionId,
		};
		api.put(endpoint, usageInfo);
	};

	prefillUserInHelpDesk = user => {
		window.FreshworksWidget('identify', 'ticketForm', {
			name: user && user.firstName,
			email: user && user.email,
		});
	};

	getLogoutUrl = () => {
		const logoutUrl = process.env.REACT_APP_LOGIN_URL;

		return `${logoutUrl}/GetJSONData.aspx?builder=Logout`;
	};

	getStore = (state = this.state) => ({
		state,
		postUsageTracking: siteArea => this.postUsageTracking(siteArea),
		saveUser: async userValuesById => this.saveUser(userValuesById),
		saveOrganization: async orgValuesById =>
			this.saveOrganization(orgValuesById),
		fetchJoinedCommunities: () => this.fetchJoinedCommunities(),
		fetchJoinedCommunitiesWithNoLoadingState: () =>
			this.fetchJoinedCommunities(false),
		getLogoutUrl: () => this.getLogoutUrl(),
		isAnonymous: () => state.isAnonymous,
		isLoading: () => state.isLoading,
		signUpUser: (formValues, handleError, onSuccess) =>
			this.signUpUser(formValues, handleError, onSuccess),
		logInUser: (formValues, handleError, onSuccess) =>
			this.logInUser(formValues, handleError, onSuccess),
		sendForgotPasswordEmail: (formValues, handleError, handleSuccess) =>
			this.sendForgotPasswordEmail(
				formValues,
				handleError,
				handleSuccess,
			),
		changeUserPassword: (formValues, handleError, handleSuccess) =>
			this.changeUserPassword(formValues, handleError, handleSuccess),
		refresh: () => this.fetchUser(),
		getUserId: () => this.getUserId(),
		isUserCommunityOwner: communityId =>
			this.isUserCommunityOwner(communityId),
		getUserCulture: () => this.getUserCulture(),
		isUnsubscribedFromFriendEmail: () =>
			state.isUnsubscribedFromFriendEmail,
		setUnsubscribedFromFriendEmailPreference: isUnsubscribed =>
			this.setUnsubscribedFromFriendEmailPreference(isUnsubscribed),
	});

	getUserId = () => this.state.user?.user?.id ?? null;

	fetchJoinedCommunities = (withLoadingState = true) => {
		withLoadingState && this.setState({ isLoading: true });
		const request = api.cancelableGet(`/api/v1/me/communities/joined`);
		request.promise.then(this.cacheFetchJoinedCommunities).catch(e => {
			console.error(e);
			withLoadingState && this.setState({ isLoading: false });
		});
		this.joinedCommunitiesFetch = request;
	};

	cacheFetchJoinedCommunities = joinedCommunities => {
		this.setState({ joinedCommunities, isLoading: false });
	};

	getUnsubscribedFromFriendEmailPreferenceUrl = () =>
		'/api/me/preferences/social/noUpdates';

	fetchUnsubscribedFromFriendEmailPreference = (withLoadingState = true) => {
		withLoadingState && this.setState({ isLoading: true });

		const url = this.getUnsubscribedFromFriendEmailPreferenceUrl();
		const request = api.cancelableGet(url);

		request.promise
			.then(this.cacheUnsubscribedFromFriendEmailPreference)
			.catch(e => {
				console.error(e);
				withLoadingState && this.setState({ isLoading: false });
			});

		this.unsubscribedFromFriendEmailPreferenceFetch = request;
	};

	cacheUnsubscribedFromFriendEmailPreference = isUnsubscribed => {
		this.setState({ isUnsubscribedFromFriendEmail: isUnsubscribed });
	};

	setUnsubscribedFromFriendEmailPreference = async isUnsubscribed => {
		const url = this.getUnsubscribedFromFriendEmailPreferenceUrl();
		let didSucceed = false;

		try {
			await api.post(url, isUnsubscribed);
			this.fetchUnsubscribedFromFriendEmailPreference(false);
			didSucceed = true;
		} catch (e) {
			this.setErrorState(e);
			didSucceed = false;
		} finally {
			return didSucceed;
		}
	};

	fetchUser = callback => {
		fetch(`${process.env.REACT_APP_API_URL}/api/v1/me/user`, {
			mode: 'cors',
			credentials: 'include',
		})
			.then(response => {
				if (response.status === 200) return response.json();
			})
			.then(jsonRes => {
				this.cacheFetchUserResponse(jsonRes);
				if (isFunction(callback)) callback();
			})
			.catch(this.runAnonUserEvents)
			.finally(() => this.setState({ isLoading: false }));
	};

	runAnonUserEvents = err => {
		console.error(err);
		setGainsightCookieForAnonUser();
		return this.setState({
			isAnonymous: true,
			isLoading: false,
			user: { user: { id: anonymousUserId } },
			region: {
				cultureName:
					window.navigator.userLanguage || window.navigator.language,
			},
		});
	};

	cacheFetchUserResponse = user => {
		this.setUser(user);
		this.prefillUserInHelpDesk(user.user);
		this.fetchRegion().then(region => {
			setGainsightIdentity(user, region, this.getSubscriberType(user));
		});
	};

	setUser(user) {
		this.setState({
			user: user,
			organization: user.organization,
			userAvatar: user.userAvatar,
			profiles: user.profiles,
			isLoading: false,
			loadingDisplay: false,
			sessionId: this.createSessionId(user.user),
			isAuthorizedToPostEventsToGenreCommunities: this.getIsAuthorizedToPostEventsToGenreCommunities(
				user,
			),
		});
	}

	fetchRegion() {
		return api
			.get('/api/v1/me/preferences/region')
			.then(region => {
				this.setState({ region });
				return region;
			})
			.catch(e => console.error(e));
	}

	getIsAuthorizedToPostEventsToGenreCommunities = user => {
		const isAuthorizedPrivilege = p =>
			p.name ===
				UserPrivilegesEnum.IsOrgAuthorizedToPostEventsToGenreCommunities ||
			p.name ===
				UserPrivilegesEnum.IsUserAuthorizedToPostEventsToGenreCommunities;
		const authorizedPrivileges =
			user?.user?.privileges?.filter(isAuthorizedPrivilege) || [];
		return authorizedPrivileges.length === 2;
	};

	isUserCommunityOwner = communityId => {
		const userId = this.getUserId();
		const community = this.state.joinedCommunities.find(
			community => Number(community.id) === Number(communityId),
		);
		return community?.ownerIds?.includes(userId) ?? false;
	};

	saveUser = async userValuesById => {
		const url = `/api/v1/users/${userValuesById.email}/`;
		let didSucceed;
		try {
			await api.post(url, userValuesById);
			this.fetchUser();
			didSucceed = true;
		} catch (e) {
			this.setErrorState(e);
			didSucceed = false;
		} finally {
			return didSucceed;
		}
	};

	saveOrganization = async orgValuesById => {
		const urlBase = process.env.REACT_APP_EPLUS_REQUEST_URL;
		const stringifiedData = queryString.stringify(orgValuesById);
		const url = `${urlBase}/GetJSONData.aspx?m=Organization&
			builder=saveAccountBits&${stringifiedData}`;

		const response = await fetch(url, {
			method: 'GET',
			credentials: 'include',
		});
		const res = await response.json();

		let didSucceed = false;
		if (res.code === 'SUCCESS') {
			this.fetchUser();
			didSucceed = true;
		}
		return didSucceed;
	};

	getSubscriberType = userAccountDetails => {
		const accountInfoForm = userAccountDetails?.accountInfoForm ?? {};
		const accountType = accountInfoForm.accountTypes?.find(
			t => t.id === accountInfoForm.selectedAccountType,
		);
		return accountType?.value;
	};

	signUpUser(formValues, handleError, onSuccess) {
		const urlBase = process.env.REACT_APP_API_URL;
		const url = `${urlBase}/api/v1/registration/user`;
		return fetch(url, {
			method: 'POST',
			body: JSON.stringify(formValues),
			headers: { 'Content-Type': 'application/json' },
		})
			.then(res => {
				if (res.status === 409) {
					handleError(<AlreadyRegisteredError />);
				} else {
					const { email, password } = formValues.user;
					this.logInUser(
						{
							email,
							pword: password,
						},
						handleError,
						onSuccess,
					);
				}
			})
			.catch(handleError);
	}

	logInUser(formValues, handleError, onSuccess) {
		const urlBase = process.env.REACT_APP_EPLUS_REQUEST_URL;
		const url = `${urlBase}/GetJSONData.aspx?builder=Login`;
		return fetch(url, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			body: new URLSearchParams(formValues).toString(),
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		})
			.then(res => res.json())
			.then(res => {
				if (res && res[0] && res[0].code === 'SUCCESS') {
					isFunction(onSuccess) && onSuccess();
				} else {
					handleError(
						<Translate id="invalid_login_please_try_again" />,
					);
				}
			})
			.catch(handleError);
	}

	sendForgotPasswordEmail(formValues, handleError, handleSuccess) {
		const urlBase = process.env.REACT_APP_EPLUS_REQUEST_URL;
		const url = `${urlBase}/GetJSONData.aspx?m=User&builder=SendForgotEmail`;
		const sendForgotEmailRequest = {
			...formValues,
			applicationIdentifier: ApplicationIdentifierEnum.Community,
		};
		return fetch(url, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			body: new URLSearchParams(sendForgotEmailRequest).toString(),
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		})
			.then(res => res.json())
			.then(res => {
				if (res && res.code === 'SUCCESS') {
					handleSuccess();
				} else {
					handleError(
						<Translate id="invalid_login_please_try_again" />,
					);
				}
			})
			.catch(handleError);
	}

	changeUserPassword(formValues, handleError, handleSuccess) {
		const urlBase = process.env.REACT_APP_EPLUS_REQUEST_URL;
		const url = `${urlBase}/GetJSONData.aspx?builder=ChangePasswordV2`;
		return fetch(url, {
			method: 'POST',
			mode: 'cors',
			credentials: 'include',
			body: new URLSearchParams(formValues).toString(),
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
		})
			.then(res => res.json())
			.then(res => {
				if (res && res.code === 'SUCCESS') {
					handleSuccess();
				} else {
					handleError(res.text);
				}
			})
			.catch(handleError);
	}

	getUserCulture = () => {
		const { region } = this.state;
		const userCulture = region?.cultureName;
		return userCulture;
	};
}

export default withIsMobile(UserDataStore);
